import React from 'react';
import { ShieldCheck, ShieldAlert, Lock, Database, Users, KeyRound, AlertTriangle } from 'lucide-react';
import { Stat, Section, Pill, fmt, bytes } from './ui';

export const OverviewTab = ({ data }) => {
  const { users, mfa, auth, backups, config_warnings: warnings, dependencies, encryption, recent_events: events, alerts_open } = data;
  const depsBackend = dependencies?.backend?.vulnerable?.length ?? null;
  const depsFrontend = dependencies?.frontend?.vulnerabilities;
  return (
    <div className="space-y-5" data-testid="security-overview">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="MFA adoption" value={`${mfa.adoption_pct}%`} sub={`${mfa.enrolled} enrolled · ${mfa.pending.length} pending · ${mfa.exempt} exempt`} tone={mfa.adoption_pct === 100 ? 'text-emerald-600' : 'text-amber-600'} testId="stat-mfa" />
        <Stat label="Failed logins (24h)" value={auth.failed_logins_24h} sub={`${auth.failed_logins_7d} in 7 days · ${auth.access_denied_24h} access denied`} tone={auth.failed_logins_24h > 10 ? 'text-rose-600' : undefined} testId="stat-failed-logins" />
        <Stat label="Last backup" value={backups.last_success ? fmt(backups.last_success.started_at) : 'None'} sub={backups.last_success ? `${backups.last_success.documents?.toLocaleString()} docs · ${bytes(backups.last_success.size_bytes)} · ${backups.last_success.restore_test_status || 'not restore-tested'}` : 'Run a backup now'} tone={backups.stale ? 'text-rose-600 text-xl' : 'text-emerald-600 text-xl'} testId="stat-backup" />
        <Stat label="Open alerts" value={alerts_open} sub={`${users.active} active staff · ${users.admins.length} admins · ${users.active_last_hour} online now`} tone={alerts_open ? 'text-rose-600' : 'text-emerald-600'} testId="stat-alerts" />
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <Section title="Protection status" testId="protection-status">
          <ul className="space-y-2.5 text-sm">
            <Row ok icon={Lock} label="Encryption in transit" sub={encryption.in_transit} />
            <Row ok={encryption.documents_at_rest} icon={Database} label="Documents encrypted at rest" sub={encryption.algorithm} />
            <Row ok icon={ShieldCheck} label="Default-deny API gate" sub="Every /api route requires a valid session unless explicitly public" />
            <Row ok icon={Users} label="Role-based access (API enforced)" sub="Admin-only prefixes, broker-support allowlist" />
            <Row ok={mfa.pending.length === 0} icon={KeyRound} label="Staff MFA (authenticator app)" sub={mfa.pending.length ? `Pending: ${mfa.pending.join(', ')}` : 'All required staff enrolled'} />
            <Row ok={!backups.stale} icon={Database} label="Encrypted backups (nightly)" sub={`${backups.retention_days}-day retention · restore-tested`} />
          </ul>
        </Section>

        <Section title="Configuration warnings" testId="config-warnings">
          {warnings.length === 0 ? <p className="text-sm text-emerald-700">No configuration warnings.</p> : (
            <ul className="space-y-2">
              {warnings.map((w, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-testid="config-warning">
                  <Pill tone={w.level}>{w.level}</Pill><span>{w.text}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Dependency vulnerabilities" testId="dependency-summary">
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between"><span className="text-slate-600">Backend (pip-audit)</span>
              {depsBackend === null ? <Pill tone="low">not scanned</Pill> : <Pill tone={depsBackend ? 'warning' : 'info'}>{depsBackend} vulnerable packages</Pill>}</div>
            <div className="flex items-center justify-between"><span className="text-slate-600">Frontend (yarn audit)</span>
              {!depsFrontend ? <Pill tone="low">not scanned</Pill> : <Pill tone={depsFrontend.critical ? 'critical' : depsFrontend.high ? 'warning' : 'info'}>{depsFrontend.critical || 0} critical · {depsFrontend.high || 0} high</Pill>}</div>
            {dependencies?.backend?.vulnerable?.slice(0, 6).map((d) => (
              <div key={d.name} className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs">
                <span className="font-mono text-slate-800">{d.name} {d.version}</span><span className="text-slate-500">fix → {d.fix_versions?.slice(-1)[0] || '—'}</span>
              </div>
            ))}
            <p className="text-xs text-slate-400">Scanned {fmt(dependencies?.backend?.generated_at)} · run <code>scripts/security_scan.sh</code> or “Re-scan” in Backups.</p>
          </div>
        </Section>
      </div>

      <Section title="Recent security events" testId="recent-events">
        <EventTable events={events} />
      </Section>
    </div>
  );
};

const Row = ({ ok, icon: Icon, label, sub }) => (
  <li className="flex items-start gap-3">
    <span className={`mt-0.5 flex h-6 w-6 items-center justify-center rounded-full ${ok ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
      {ok ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
    </span>
    <div><div className="font-medium text-slate-800 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 text-slate-400" />{label}</div><div className="text-xs text-slate-500">{sub}</div></div>
  </li>
);

export const EventTable = ({ events, compact }) => (
  <div className="overflow-x-auto -mx-2">
    <table className="min-w-full text-sm">
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
        <th className="px-2 py-1.5">When</th><th className="px-2 py-1.5">Action</th><th className="px-2 py-1.5">Actor</th>{!compact && <th className="px-2 py-1.5">Record</th>}<th className="px-2 py-1.5">IP</th><th className="px-2 py-1.5">Outcome</th>
      </tr></thead>
      <tbody>
        {events.length === 0 && <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-400">No events yet</td></tr>}
        {events.map((e) => (
          <tr key={e.id} className="border-t border-slate-100" data-testid="audit-event-row">
            <td className="px-2 py-1.5 whitespace-nowrap text-slate-500 tabular-nums">{fmt(e.at)}</td>
            <td className="px-2 py-1.5"><span className="font-mono text-xs text-slate-800">{e.action}</span>{e.severity !== 'info' && <span className="ml-1.5"><Pill tone={e.severity}>{e.severity}</Pill></span>}</td>
            <td className="px-2 py-1.5 text-slate-700">{e.actor?.email || (e.client?.client_id ? `client:${e.client.client_id.slice(0, 8)}` : <span className="text-slate-400">anonymous</span>)}</td>
            {!compact && <td className="px-2 py-1.5 text-xs text-slate-500 max-w-[260px] truncate">{e.entity_id || e.path || ''}</td>}
            <td className="px-2 py-1.5 font-mono text-xs text-slate-500">{e.ip || '—'}</td>
            <td className="px-2 py-1.5">{e.outcome === 'success' ? <span className="text-emerald-600 text-xs font-semibold">ok</span> : <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-semibold"><AlertTriangle className="h-3 w-3" />{e.outcome}</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
